import React, { useState, useEffect, useMemo } from "react";
import { api } from "@/lib/api";
import { useSearchParams } from "react-router-dom";
import { PageHeader, Table, Badge, Card, Modal, Field, Input, Select, Btn, useDirtyGuard, ConfirmClose } from "@/components/erp/ui";
import { KonsultanToday } from "@/components/erp/KonsultanToday";
import { KonsultanRecap } from "@/components/erp/KonsultanRecap";
import { toast } from "sonner";
import { CalendarX, AlertTriangle, ChevronLeft, ChevronRight, Plus, Upload, FileText, MessageCircle, Clock, Search, X } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const REASONS = ["Public Holiday", "Cuti Bersama", "Company Holiday", "Consultant Unavailable", "Team Adjustment", "Internal Meeting", "Training", "Maintenance", "Other"];
const DAYS = ["Min", "Sen", "Sel", "Rab", "Kam", "Jum", "Sab"];
const MONTHS = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
const TAMU_STATIC_1 = ["1. REMODEL / DESIGN", ["3D PRINT", "TRACING/MANUAL +EDITING (STANDAR)", "TRACING/MANUAL +EDITING (INTERMEDIATE)", "3D SCANNING +EDITING (STANDAR)", "3D SCANNING +EDITING (INTERMEDIATE)"]];
const TAMU_STATIC_FINISH = ["3. FINISHING", ["TREATMENT (amplas dempul)", "CAT", "STIKER", "RUBBER COMPLEX"]];
const matLabel = (m) => `${(m.name || "").trim()} ${Number(m.price_per_gram || 0).toLocaleString("id-ID")}/gr`;
const waNumber = (s) => { let d = String(s || "").replace(/\D/g, ""); if (d.startsWith("0")) d = "62" + d.slice(1); else if (d && !d.startsWith("62")) d = "62" + d; return d; };

export default function BookingAdmin() {
  const [tab, setTab] = useState("calendar");
  const [bookings, setBookings] = useState([]);
  const [blocked, setBlocked] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ date: "", end_date: "", reason: "Public Holiday", notes: "" });
  const [warn, setWarn] = useState(null);
  const [month, setMonth] = useState(new Date());
  const [dayView, setDayView] = useState(null);
  const [dayAvail, setDayAvail] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ time: "", name: "", phone: "", email: "", notes: "" });
  const [detail, setDetail] = useState(null);
  const [rs, setRs] = useState({ date: "", time: "" });
  const [ket, setKet] = useState({ keterangan: "", conversation: "", services: [] });
  const [reminders, setReminders] = useState([]);
  const [materials, setMaterials] = useState([]);
  const tamuSections = useMemo(() => {
    const fdm = materials.filter((m) => (m.process || "FDM").toUpperCase() === "FDM").map(matLabel);
    const resin = materials.filter((m) => ["SLA", "DSLA", "RESIN"].includes((m.process || "").toUpperCase())).map(matLabel);
    return [TAMU_STATIC_1, ["2. PRODUKSI - FDM (FILAMEN)", fdm], ["2. PRODUKSI - DSLA (RESIN)", resin], TAMU_STATIC_FINISH];
  }, [materials]);
  const [bkQuery, setBkQuery] = useState("");
  const [nearQuery, setNearQuery] = useState("");
  const [bkSort, setBkSort] = useState("newest");
  const gBlk = useDirtyGuard();
  const gAdd = useDirtyGuard();
  const gKet = useDirtyGuard();
  const forceCloseBlk = () => { gBlk.clear(); setOpen(false); setForm({ date: "", end_date: "", reason: "Public Holiday", notes: "" }); };
  const tryCloseBlk = () => gBlk.attemptClose(form, forceCloseBlk);
  const forceCloseAdd = () => { gAdd.clear(); setAddOpen(false); setAddForm({ time: "", name: "", phone: "", email: "", notes: "" }); };
  const tryCloseAdd = () => gAdd.attemptClose(addForm, forceCloseAdd);
  const tryCloseKet = () => gKet.attemptClose(ket, () => { gKet.clear(); setDetail(null); });
  const filteredBookings = useMemo(() => {
    const q = bkQuery.trim().toLowerCase();
    let r = [...bookings];
    if (q) r = r.filter((b) => [b.code, b.name, b.phone, b.email, b.visit_type].some((v) => (v || "").toLowerCase().includes(q)));
    if (bkSort === "name_az") r.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    else if (bkSort === "name_za") r.sort((a, b) => (b.name || "").localeCompare(a.name || ""));
    else if (bkSort === "oldest") r.sort((a, b) => `${a.date} ${a.time}`.localeCompare(`${b.date} ${b.time}`));
    else r.sort((a, b) => `${b.date} ${b.time}`.localeCompare(`${a.date} ${a.time}`));
    return r;
  }, [bookings, bkQuery, bkSort]);

  const loadReminders = () => api.get("/bookings/reminders?day=tomorrow").then(({ data }) => setReminders(data.bookings)).catch(() => {});
  useEffect(() => { if (tab === "reminder") loadReminders(); }, [tab]);
  const sendReminder = async (b) => {
    const txt = `Halo ${b.name}, mengingatkan jadwal kunjungan Anda ke ${b.branch_name || "FOMU 3D PRINT"} besok ${b.date} pukul ${b.time} (${b.visit_type || "Konsultasi"}). Sampai jumpa!`;
    window.open(`https://wa.me/${waNumber(b.phone)}?text=${encodeURIComponent(txt)}`, "_blank");
    try { await api.put(`/bookings/${b.id}/remind`); toast.success("Reminder dikirim"); loadReminders(); } catch {}
  };

  const load = () => {
    api.get("/bookings").then(({ data }) => setBookings(data)).catch(() => {});
    api.get("/blocked-dates").then(({ data }) => setBlocked(data)).catch(() => {});
    api.get("/materials").then(({ data }) => setMaterials(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const [sp, setSp] = useSearchParams();
  const codeParam = sp.get("code");
  useEffect(() => {
    if (!codeParam || !bookings.length) return;
    const b = bookings.find((x) => x.code === codeParam);
    if (b) openDetail(b); else toast.error(`Booking ${codeParam} tidak ditemukan`);
    setSp({}, { replace: true });
  }, [codeParam, bookings]); // eslint-disable-line react-hooks/exhaustive-deps

  const block = async () => {
    if (!form.date) { toast.error("Tanggal wajib"); return; }
    const { data } = await api.post("/blocked-dates", form);
    if (data.affected?.length) setWarn(data.affected);
    toast.success("Tanggal diblokir"); setOpen(false); setForm({ date: "", end_date: "", reason: "Public Holiday", notes: "" }); load();
  };
  const unblock = async (id) => { await api.delete(`/blocked-dates/${id}`); load(); };

  const y = month.getFullYear(), m = month.getMonth();
  const firstDay = new Date(y, m, 1).getDay();
  const daysInMonth = new Date(y, m + 1, 0).getDate();
  const blockedSet = new Set(blocked.map((b) => b.date));
  const countByDate = bookings.reduce((acc, b) => { acc[b.date] = (acc[b.date] || 0) + 1; return acc; }, {});
  const nowStr = new Date().toISOString().slice(0, 10);
  const upcoming = bookings.filter((b) => b.date >= nowStr && b.status !== "CANCELLED").sort((a, b) => (a.date + a.time).localeCompare(b.date + b.time));
  const nq = nearQuery.trim().toLowerCase();
  const upcomingFiltered = nq ? upcoming.filter((b) => [b.code, b.name, b.phone, b.email, b.visit_type, b.date].some((v) => (v || "").toLowerCase().includes(nq))) : upcoming;

  const openDay = async (ds) => {
    setDayView(ds); setDayAvail(null);
    try { const { data } = await api.get(`/booking/availability?date_str=${ds}`); setDayAvail(data); } catch {}
  };
  const addBooking = async () => {
    if (!addForm.time || !addForm.name || !addForm.phone) { toast.error("Isi jam, nama & telepon"); return; }
    try {
      await api.post("/booking", { date: dayView, ...addForm });
      toast.success("Booking ditambahkan"); setAddOpen(false);
      setAddForm({ time: "", name: "", phone: "", email: "", notes: "" }); load(); openDay(dayView);
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };
  const dayBookings = bookings.filter((b) => b.date === dayView);

  const openDetail = async (b) => {
    try {
      const { data } = await api.get(`/bookings/${b.id}`);
      setDetail(data); setRs({ date: data.date, time: data.time });
      const k = { keterangan: data.keterangan || "", conversation: data.conversation || "", services: data.services || [] };
      setKet(k); gKet.snapshot(k);
    } catch { toast.error("Gagal memuat detail"); }
  };
  const toggleService = (s) => setKet((k) => ({ ...k, services: k.services.includes(s) ? k.services.filter((x) => x !== s) : [...k.services, s] }));
  const saveKet = async () => {
    try { const { data } = await api.put(`/bookings/${detail.id}/keterangan`, ket); setDetail({ ...detail, ...data }); toast.success("Keterangan tersimpan"); return true; }
    catch { toast.error("Gagal menyimpan"); return false; }
  };
  const uploadPhoto = async (e) => {
    const files = Array.from(e.target.files || []); e.target.value = "";
    if (!files.length) return;
    let photos = [...(detail.photos || [])];
    for (const file of files) {
      const fd = new FormData(); fd.append("file", file);
      try { const { data } = await api.post(`/bookings/${detail.id}/photo`, fd); photos = [...photos, data]; }
      catch { toast.error(`Gagal unggah ${file.name}`); }
    }
    setDetail({ ...detail, photos });
    toast.success("Foto diunggah");
  };
  const deletePhoto = async (pid) => {
    try { await api.delete(`/bookings/${detail.id}/photo/${pid}`); setDetail({ ...detail, photos: (detail.photos || []).filter((p) => p.id !== pid) }); toast.success("Foto dihapus"); }
    catch { toast.error("Gagal hapus foto"); }
  };
  const doReschedule = async () => {
    if (!rs.date || !rs.time) { toast.error("Isi tanggal & jam baru"); return; }
    try {
      await api.put(`/bookings/${detail.id}/reschedule`, { date: rs.date, time: rs.time, reason: "Admin reschedule" });
      toast.success("Jadwal diubah"); load();
      const txt = `Halo ${detail.name}, kunjungan Anda ke FOMU 3D PRINT dijadwalkan ulang ke ${rs.date} pukul ${rs.time}. Terima kasih.`;
      window.open(`https://wa.me/${waNumber(detail.phone)}?text=${encodeURIComponent(txt)}`, "_blank");
      setDetail(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal reschedule"); }
  };
  const printGuestSheet = () => window.open(`${BACKEND}/api/bookings/${detail.id}/guest-sheet/pdf`, "_blank");
  const doCancel = async () => {
    const reason = (window.prompt("Alasan pembatalan booking (wajib):") || "").trim();
    if (reason.length < 5) { toast.error("Alasan pembatalan wajib diisi (min. 5 huruf)"); return; }
    if (!window.confirm(`Batalkan booking ${detail.name} (${detail.date} ${detail.time})?`)) return;
    try { await api.post(`/bookings/${detail.id}/cancel`, { reason }); toast.success("Booking dibatalkan"); load(); setDetail(null); } catch (e) { toast.error(e.response?.data?.detail || "Gagal membatalkan"); }
  };

  return (
    <div>
      <PageHeader title="Booking Management" subtitle="Kalender, daftar waktu terdekat, rekam kunjungan & reschedule." action={<Btn data-testid="add-blocked-btn" onClick={() => { const f = { date: "", end_date: "", reason: "Public Holiday", notes: "" }; setForm(f); gBlk.snapshot(f); setOpen(true); }}><CalendarX className="w-4 h-4" /> Blokir Tanggal</Btn>} />

      {warn && (
        <Card className="p-4 mb-4 border-amber-300 bg-amber-50">
          <div className="flex items-center gap-2 text-amber-700 font-medium text-sm"><AlertTriangle className="w-4 h-4" /> Ada booking pada tanggal yang diblokir</div>
          <div className="text-sm text-amber-700 mt-1">{warn.map((a) => `${a.date}: ${a.count} booking`).join(" · ")} — perlu reschedule.</div>
        </Card>
      )}

      <div className="flex gap-2 mb-4 flex-wrap">
        {[["calendar", "Kalender"], ["konsul", "Jadwal Konsultasi"], ["konsulrecap", "Rekap Konsultan"], ["nearest", "Terdekat"], ["reminder", "Reminder H-1"], ["bookings", "Semua Reservasi"], ["blocked", "Blocked Dates"]].map(([k, l]) => (
          <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)} className={`px-4 py-2 rounded-lg text-sm font-medium ${tab === k ? "bg-slate-900 text-white" : "border border-slate-300"}`}>{l}</button>
        ))}
      </div>

      {tab === "konsul" && <KonsultanToday picker />}
      {tab === "konsulrecap" && <KonsultanRecap />}

      {tab === "calendar" && (
        <Card className="p-5">
          <div className="flex items-center justify-between mb-4">
            <button data-testid="cal-prev" onClick={() => setMonth(new Date(y, m - 1, 1))} className="w-9 h-9 rounded-lg border border-slate-200 flex items-center justify-center"><ChevronLeft className="w-4 h-4" /></button>
            <span className="font-head font-semibold">{MONTHS[m]} {y}</span>
            <button data-testid="cal-next" onClick={() => setMonth(new Date(y, m + 1, 1))} className="w-9 h-9 rounded-lg border border-slate-200 flex items-center justify-center"><ChevronRight className="w-4 h-4" /></button>
          </div>
          <div className="grid grid-cols-7 gap-1 text-center text-xs text-slate-400 mb-2">{DAYS.map((d) => <div key={d}>{d}</div>)}</div>
          <div className="grid grid-cols-7 gap-1">
            {Array.from({ length: firstDay }).map((_, i) => <div key={"e" + i} />)}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1;
              const ds = `${y}-${String(m + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
              const cnt = countByDate[ds] || 0;
              const isBlocked = blockedSet.has(ds);
              return (
                <button key={day} data-testid={`cal-day-${day}`} onClick={() => openDay(ds)}
                  className={`aspect-square rounded-lg text-sm flex flex-col items-center justify-center transition-colors border ${isBlocked ? "bg-rose-50 border-rose-200 text-rose-500" : "border-transparent hover:bg-sky-50"}`}>
                  <span>{day}</span>
                  {cnt > 0 && <span className="mt-0.5 text-[10px] font-mono bg-sky-500 text-white rounded-full px-1.5">{cnt}</span>}
                  {isBlocked && cnt === 0 && <span className="text-[9px]">tutup</span>}
                </button>
              );
            })}
          </div>
        </Card>
      )}

      {tab === "nearest" && (
        <Card className="p-5">
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <div className="flex items-center gap-2 text-xs font-mono text-slate-500"><Clock className="w-3.5 h-3.5" /> URUT DARI WAKTU TERDEKAT</div>
            <div className="relative flex-1 min-w-[200px]">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input data-testid="nearest-search" value={nearQuery} onChange={(e) => setNearQuery(e.target.value)} placeholder="Cari nama, telepon, kode, kebutuhan, tanggal…" className="w-full border border-slate-200 rounded-lg pl-9 pr-3 py-2 text-sm" />
            </div>
          </div>
          {upcomingFiltered.length === 0 ? <p className="text-sm text-slate-400">{nq ? "Tidak ada hasil yang cocok." : "Belum ada reservasi mendatang."}</p> : (
            <div className="space-y-2">
              {upcomingFiltered.map((b) => (
                <button key={b.id} data-testid={`nearest-${b.id}`} onClick={() => openDetail(b)} className="w-full flex items-center gap-3 text-left border border-slate-200 rounded-xl px-4 py-3 hover:border-sky-300 hover:bg-sky-50/40 transition-colors">
                  <div className="text-center w-16"><div className="text-xs text-slate-500">{b.date.slice(5)}</div><div className="font-mono font-semibold">{b.time}</div></div>
                  <div className="flex-1"><div className="font-medium text-sm">{b.name}</div><div className="text-xs text-slate-500">{b.visit_type || "Konsultasi"} · {b.phone}{b.branch_name ? ` · ${b.branch_name}` : ""}</div></div>
                  <Badge>{b.status}</Badge>
                </button>
              ))}
            </div>
          )}
        </Card>
      )}

      {tab === "reminder" && (
        <Card className="p-5">
          <div className="flex items-center gap-2 text-xs font-mono text-slate-500 mb-3"><Clock className="w-3.5 h-3.5" /> KUNJUNGAN BESOK (H-1) — KIRIM REMINDER WHATSAPP</div>
          {reminders.length === 0 ? <p className="text-sm text-slate-400">Tidak ada kunjungan besok.</p> : (
            <div className="space-y-2">
              {reminders.map((b) => (
                <div key={b.id} data-testid={`reminder-${b.id}`} className="flex items-center gap-3 border border-slate-200 rounded-xl px-4 py-3">
                  <div className="text-center w-16"><div className="font-mono font-semibold">{b.time}</div></div>
                  <div className="flex-1"><div className="font-medium text-sm">{b.name}</div><div className="text-xs text-slate-500">{b.visit_type || "Konsultasi"} · {b.phone}{b.branch_name ? ` · ${b.branch_name}` : ""}</div></div>
                  {b.reminded_at && <Badge>Sudah diingatkan</Badge>}
                  <Btn data-testid={`send-reminder-${b.id}`} onClick={() => sendReminder(b)}><MessageCircle className="w-4 h-4" /> Reminder WA</Btn>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {tab === "bookings" && (
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2 items-center">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input data-testid="booking-search" value={bkQuery} onChange={(e) => setBkQuery(e.target.value)} placeholder="Cari kode, nama, telepon, email, kebutuhan…" className="w-full border border-slate-200 rounded-lg pl-9 pr-3 py-2 text-sm" />
            </div>
            <select data-testid="booking-sort" value={bkSort} onChange={(e) => setBkSort(e.target.value)} className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white">
              <option value="newest">Terbaru</option>
              <option value="oldest">Terlama</option>
              <option value="name_az">Nama A-Z</option>
              <option value="name_za">Nama Z-A</option>
            </select>
          </div>
          <Table search={false} testid="bookings-table" columns={["Kode", "Tanggal", "Jam", "Nama", "No Telepon", "Kebutuhan", "Status", ""]} rows={filteredBookings} renderRow={(b) => (
            <tr key={b.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
              <td className="px-4 py-3 font-mono text-xs">{b.code}</td>
              <td className="px-4 py-3">{b.date}</td>
              <td className="px-4 py-3">{b.time}</td>
              <td className="px-4 py-3 font-medium">{b.name}</td>
              <td className="px-4 py-3 text-slate-600 text-xs font-mono" data-testid={`booking-phone-${b.id}`}>{b.phone || "-"}</td>
              <td className="px-4 py-3 text-slate-500 text-xs">{b.visit_type || "-"}</td>
              <td className="px-4 py-3"><Badge>{b.status}</Badge></td>
              <td className="px-4 py-3 text-right"><button data-testid={`booking-detail-${b.id}`} onClick={() => openDetail(b)} className="text-sky-600 text-sm hover:underline">Detail</button></td>
            </tr>
          )} />
        </div>
      )}

      {tab === "blocked" && (
        <Table testid="blocked-table" columns={["Tanggal", "Alasan", "Catatan", "Oleh", ""]} rows={blocked} renderRow={(b) => (
          <tr key={b.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
            <td className="px-4 py-3 font-medium">{b.date}</td>
            <td className="px-4 py-3">{b.reason}</td>
            <td className="px-4 py-3 text-slate-500">{b.notes || "-"}</td>
            <td className="px-4 py-3 text-slate-500 text-xs">{b.by}</td>
            <td className="px-4 py-3 text-right"><button onClick={() => unblock(b.id)} className="text-rose-500 text-sm hover:underline">Hapus</button></td>
          </tr>
        )} />
      )}

      {/* Day view modal */}
      <Modal open={!!dayView} onClose={() => setDayView(null)} title={`Detail Hari · ${dayView}`}
        footer={<Btn data-testid="day-add-booking" onClick={() => { const f = { time: "", name: "", phone: "", email: "", notes: "" }; setAddForm(f); gAdd.snapshot(f); setAddOpen(true); }}><Plus className="w-4 h-4" /> Tambah Booking</Btn>}>
        <div className="space-y-4">
          <div>
            <div className="text-xs font-mono text-slate-500 mb-2">RESERVASI ({dayBookings.length})</div>
            {dayBookings.length === 0 ? <p className="text-sm text-slate-400">Belum ada reservasi.</p> :
              dayBookings.map((b) => (
                <button key={b.id} onClick={() => { setDayView(null); openDetail(b); }} className="w-full flex items-center justify-between border-b border-slate-100 py-2 text-sm text-left hover:bg-slate-50">
                  <span className="font-mono">{b.time}</span><span className="font-medium flex-1 px-3">{b.name}</span>
                  <span className="text-slate-500">{b.phone}</span><Badge>{b.status}</Badge>
                </button>
              ))}
          </div>
          {dayAvail && dayAvail.status === "OPEN" && (
            <div>
              <div className="text-xs font-mono text-slate-500 mb-2">SLOT TERSEDIA</div>
              <div className="flex flex-wrap gap-1.5">
                {(dayAvail.slots || []).map((s) => (<span key={s.time} title={s.busy ? "Ramai — ada antrian tamu di jam ini" : ""} className={`relative text-xs px-2 py-1 rounded-md ${!s.available ? "bg-slate-100 text-slate-400 line-through" : s.busy ? "bg-amber-100 text-amber-700" : "bg-emerald-50 text-emerald-700"}`}>{s.time}{s.busy && <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-amber-500" />}</span>))}
              </div>
            </div>
          )}
          {dayAvail && dayAvail.status !== "OPEN" && <p className="text-sm text-rose-500">{dayAvail.reason}</p>}
        </div>
      </Modal>

      {/* Add booking modal */}
      <Modal open={addOpen} onClose={tryCloseAdd} title={`Tambah Booking · ${dayView}`}
        footer={<><Btn variant="ghost" onClick={tryCloseAdd}>Batal</Btn><Btn data-testid="confirm-add-booking" onClick={addBooking}>Simpan</Btn></>}>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Jam"><Select data-testid="add-time" value={addForm.time} onChange={(e) => setAddForm({ ...addForm, time: e.target.value })}>
            <option value="">— pilih —</option>
            {(dayAvail?.slots || []).filter((s) => s.available).map((s) => <option key={s.time} value={s.time}>{s.time}</option>)}
          </Select></Field>
          <Field label="Nama"><Input data-testid="add-name" value={addForm.name} onChange={(e) => setAddForm({ ...addForm, name: e.target.value })} /></Field>
          <Field label="Telepon"><Input data-testid="add-phone" value={addForm.phone} onChange={(e) => setAddForm({ ...addForm, phone: e.target.value })} /></Field>
          <Field label="Email"><Input value={addForm.email} onChange={(e) => setAddForm({ ...addForm, email: e.target.value })} /></Field>
        </div>
      </Modal>

      {/* Block modal */}
      <Modal open={open} onClose={tryCloseBlk} title="Blokir Tanggal"
        footer={<><Btn variant="ghost" onClick={tryCloseBlk}>Batal</Btn><Btn data-testid="save-blocked-btn" onClick={block}>Blokir</Btn></>}>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Tanggal *"><Input type="date" data-testid="blk-date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></Field>
          <Field label="Sampai (opsional)"><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></Field>
          <div className="col-span-2"><Field label="Alasan"><Select value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })}>{REASONS.map(r => <option key={r}>{r}</option>)}</Select></Field></div>
          <div className="col-span-2"><Field label="Catatan"><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field></div>
        </div>
      </Modal>

      {/* Booking detail / record modal */}
      <Modal open={!!detail} onClose={tryCloseKet} title={`Kunjungan · ${detail?.code}`}>
        {detail && (
          <div className="space-y-5" data-testid="booking-detail">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><div className="text-xs text-slate-500">Nama</div>{detail.name}</div>
              <div><div className="text-xs text-slate-500">Jadwal</div>{detail.date} · {detail.time}</div>
              <div><div className="text-xs text-slate-500">Telepon</div>{detail.phone}</div>
              <div><div className="text-xs text-slate-500">Email</div>{detail.email || "-"}</div>
              <div><div className="text-xs text-slate-500">Kebutuhan</div>{detail.visit_type || "-"}</div>
              <div><div className="text-xs text-slate-500">Customer</div>{detail.customer ? <a className="text-sky-600 hover:underline" href="/app/crm">{detail.customer.name} ({detail.customer.code})</a> : "—"}</div>
            </div>

            {/* Reschedule */}
            <div className="border-t border-slate-100 pt-3">
              <div className="text-xs font-mono text-slate-500 mb-2">RESCHEDULE + NOTIFIKASI WA</div>
              <div className="flex gap-2 items-end">
                <Field label="Tanggal baru"><Input type="date" data-testid="rs-date" value={rs.date} onChange={(e) => setRs({ ...rs, date: e.target.value })} /></Field>
                <Field label="Jam baru"><Input data-testid="rs-time" placeholder="13:00" value={rs.time} onChange={(e) => setRs({ ...rs, time: e.target.value })} /></Field>
                <Btn data-testid="reschedule-btn" disabled={detail.status === "CANCELLED"} onClick={doReschedule}><MessageCircle className="w-4 h-4" /> Ubah & Kabari</Btn>
              </div>
              {detail.status === "CANCELLED" ? <div className="mt-2 text-xs text-rose-700" data-testid="booking-cancel-info">Dibatalkan {detail.cancelled_by} · {detail.cancelled_at ? new Date(detail.cancelled_at).toLocaleString("id-ID", { timeZone: "Asia/Jakarta" }) : ""} · alasan: {detail.cancel_reason}</div>
                : <button type="button" data-testid="booking-cancel-btn" onClick={doCancel} className="mt-2 text-xs text-rose-600 underline">Batalkan booking (wajib isi alasan)</button>}
            </div>

            {/* Keterangan / template tamu */}
            <div className="border-t border-slate-100 pt-3">
              <div className="text-xs font-mono text-slate-500 mb-2">LEMBAR TAMU — LAYANAN DIMINATI</div>
              <div className="space-y-2">
                {tamuSections.map(([title, opts]) => (
                  <div key={title}>
                    <div className="text-xs font-semibold text-slate-600">{title}</div>
                    <div className="grid grid-cols-2 gap-1 mt-1">
                      {opts.length === 0 ? <div className="text-xs text-slate-400 italic col-span-2">Belum ada material di Settings.</div> : opts.map((o) => (
                        <label key={o} className="flex items-center gap-1.5 text-xs text-slate-600">
                          <input type="checkbox" checked={ket.services.includes(o)} onChange={() => toggleService(o)} className="accent-sky-600" /> {o}
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-3 space-y-2">
                <Field label="Ringkasan Percakapan (paste dari chat)"><textarea data-testid="ket-conversation" rows={3} value={ket.conversation} onChange={(e) => setKet({ ...ket, conversation: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" /></Field>
                <Field label="Ket / Note"><textarea data-testid="ket-note" rows={2} value={ket.keterangan} onChange={(e) => setKet({ ...ket, keterangan: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" /></Field>
              </div>
              <div className="border-t border-slate-100 pt-3 mt-3" data-testid="dokumentasi-foto">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-mono text-slate-500">DOKUMENTASI FOTO ({(detail.photos || []).length})</div>
                  <label data-testid="upload-photo" className="inline-flex items-center gap-1.5 text-sm text-sky-600 cursor-pointer hover:underline"><Upload className="w-4 h-4" /> Unggah<input type="file" accept="image/*" multiple className="hidden" onChange={uploadPhoto} /></label>
                </div>
                {(detail.photos || []).length === 0 && <p className="text-xs text-slate-400 mb-1">Belum ada foto — unggah dokumentasi hasil kunjungan; otomatis tampil juga di Riwayat Kunjungan customer.</p>}
                <div className="grid grid-cols-3 gap-2">
                  {(detail.photos || []).map((p) => (
                    <div key={p.id} className="relative group" data-testid={`photo-${p.id}`}>
                      <a href={`${BACKEND}/api/files/${p.path}`} target="_blank" rel="noreferrer" title={p.note || p.filename}>
                        <img src={`${BACKEND}/api/files/${p.path}`} alt={p.filename} className="w-full h-24 object-cover rounded-lg border border-slate-200" />
                      </a>
                      {p.note && <div className="text-[10px] text-slate-500 mt-0.5 leading-tight line-clamp-2">{p.note}</div>}
                      <button onClick={() => deletePhoto(p.id)} data-testid={`photo-del-${p.id}`} className="absolute -top-1.5 -right-1.5 bg-rose-500 text-white rounded-full w-4 h-4 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"><X className="w-2.5 h-2.5" /></button>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex gap-2 mt-3">
                <Btn data-testid="save-ket-btn" onClick={saveKet}>Simpan Keterangan</Btn>
                <Btn variant="ghost" data-testid="print-guest-sheet" onClick={printGuestSheet}><FileText className="w-4 h-4" /> Cetak Lembar Tamu</Btn>
                <Btn variant="ghost" data-testid="download-guest-docx" onClick={() => window.open(`${BACKEND}/api/bookings/${detail.id}/guest-sheet/docx`, "_blank")}><FileText className="w-4 h-4" /> Download .docx</Btn>
              </div>
            </div>
          </div>
        )}
      </Modal>
      <ConfirmClose open={gBlk.confirmOpen} testid="blk-confirm-close" onCancel={() => gBlk.setConfirmOpen(false)} onDiscard={forceCloseBlk} onSave={async () => { gBlk.setConfirmOpen(false); await block(); }} />
      <ConfirmClose open={gAdd.confirmOpen} testid="addbk-confirm-close" onCancel={() => gAdd.setConfirmOpen(false)} onDiscard={forceCloseAdd} onSave={async () => { gAdd.setConfirmOpen(false); await addBooking(); }} />
      <ConfirmClose open={gKet.confirmOpen} testid="ket-confirm-close" onCancel={() => gKet.setConfirmOpen(false)} onDiscard={() => { gKet.clear(); setDetail(null); }} onSave={async () => { gKet.setConfirmOpen(false); const ok = await saveKet(); if (ok) { gKet.clear(); setDetail(null); } }} />
    </div>
  );
}
